/**
 * PicksTableSection Component
 * Sortable table with responsive column visibility and color-coded badges
 * Includes hover effects, striping, and click-to-detail functionality
 * Supports both leg-level and parlay-level views
 */

import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type {
  GroupBy,
  ParlayRecord,
  ParlaySort,
  PickRecord,
  SortBy,
  SortDirection,
} from '@server/services/admin/adminPicksTypes.ts';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, DollarSign, Star } from 'lucide-react';

interface PicksTableSectionProps {
  picks: PickRecord[];
  parlays: ParlayRecord[];
  groupBy: GroupBy;
  sortBy: SortBy | ParlaySort;
  sortDirection: SortDirection;
  loading: boolean;
  onSort: (field: SortBy | ParlaySort) => void;
  onRowClick: (pickId: string) => void;
}

export function PicksTableSection({
  picks,
  parlays,
  groupBy,
  sortBy,
  sortDirection,
  loading,
  onSort,
  onRowClick,
}: PicksTableSectionProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-theme-border bg-theme-surface p-8">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-theme-border"></div>
          ))}
        </div>
      </div>
    );
  }

  const dataLength = groupBy === 'parlay' ? (parlays?.length ?? 0) : (picks?.length ?? 0);

  if (dataLength === 0 && !loading) {
    return (
      <div className="rounded-lg border border-theme-border bg-theme-surface p-8 text-center">
        <p className="text-theme-text-secondary">
          No {groupBy === 'parlay' ? 'parlays' : 'picks'} found matching your filters.
        </p>
      </div>
    );
  }

  const getSortIcon = (field: SortBy | ParlaySort) => {
    if (sortBy !== field) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  if (groupBy === 'parlay') {
    return (
      <div className="overflow-x-auto rounded-lg border border-theme-border bg-theme-surface">
        <table className="w-full">
          <thead className="border-b border-theme-border bg-theme-bg">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
                Parlay ID
              </th>
              <SortableHeader
                label="Legs"
                field="leg_count"
                sortBy={sortBy as ParlaySort}
                onSort={onSort}
                icon={getSortIcon('leg_count')}
              />
              <SortableHeader
                label="Hit Rate"
                field="hit_rate"
                sortBy={sortBy as ParlaySort}
                onSort={onSort}
                icon={getSortIcon('hit_rate')}
                className="hidden @3xl:table-cell"
              />
              <th className="hidden px-4 py-3 text-left text-xs font-medium text-theme-text-secondary @5xl:table-cell">
                Avg Strategy
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
                Result
              </th>
              <SortableHeader
                label="Total Cost"
                field="total_cost"
                sortBy={sortBy as ParlaySort}
                onSort={onSort}
                icon={getSortIcon('total_cost')}
                className="hidden @3xl:table-cell"
              />
              <th className="hidden px-4 py-3 text-left text-xs font-medium text-theme-text-secondary @5xl:table-cell">
                Mode
              </th>
              <SortableHeader
                label="Created"
                field="created_at"
                sortBy={sortBy as ParlaySort}
                onSort={onSort}
                icon={getSortIcon('created_at')}
              />
            </tr>
          </thead>
          <tbody>
            {(parlays || []).map((parlay, index) => (
              <tr
                key={parlay.groupKey}
                onClick={() => {
                  // Click first leg to show detail
                  if (parlay.legs.length > 0) {
                    onRowClick(parlay.legs[0].id);
                  }
                }}
                className={`cursor-pointer transition-colors hover:bg-theme-bg ${
                  index % 2 === 0 ? 'bg-theme-surface' : 'bg-theme-bg/50'
                }`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-theme-text">
                      {parlay.parlayId
                        ? `Parlay #${parlay.parlayId}`
                        : `Gen ${parlay.generationId?.slice(0, 8)}`}
                    </span>
                    {parlay.wasTracked && (
                      <Star className="h-3 w-3 fill-theme-warning text-theme-warning" />
                    )}
                    {parlay.wasBetOn && <DollarSign className="h-3 w-3 text-theme-success" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-theme-text">{parlay.legCount}</td>
                <td className="hidden px-4 py-3 text-sm text-theme-text @3xl:table-cell">
                  {parlay.settledCount > 0
                    ? `${parlay.hitCount}/${parlay.settledCount} (${((parlay.hitCount / parlay.settledCount) * 100).toFixed(1)}%)`
                    : '-'}
                </td>
                <td className="hidden px-4 py-3 text-sm text-theme-text @5xl:table-cell">
                  {parlay.avgStrategyScore.toFixed(1)}
                </td>
                <td className="px-4 py-3">
                  <ParlayResultBadge
                    outcomeResolved={parlay.outcomeResolved}
                    parlayHit={parlay.parlayHit}
                    hitCount={parlay.hitCount}
                    legCount={parlay.legCount}
                    settledCount={parlay.settledCount}
                  />
                </td>
                <td className="hidden px-4 py-3 text-sm text-theme-text @3xl:table-cell">
                  ${parlay.totalCost.toFixed(2)}
                </td>
                <td className="hidden px-4 py-3 @5xl:table-cell">
                  {parlay.analysisMode && <ModeBadge mode={parlay.analysisMode} />}
                </td>
                <td className="px-4 py-3 text-sm text-theme-text-secondary">
                  {format(new Date(parlay.createdAt), 'MMM d, h:mma')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Leg view
  return (
    <div className="overflow-x-auto rounded-lg border border-theme-border bg-theme-surface">
      <table className="w-full">
        <thead className="border-b border-theme-border bg-theme-bg">
          <tr>
            <SortableHeader
              label="Player"
              field="player_name"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('player_name')}
              className="min-w-[150px]"
            />
            <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
              Prop
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
              Line
            </th>
            <th className="hidden px-4 py-3 text-left text-xs font-medium text-theme-text-secondary @3xl:table-cell">
              Predicted
            </th>
            <SortableHeader
              label="Edge %"
              field="edge_pct"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('edge_pct')}
              className="hidden @3xl:table-cell"
            />
            <SortableHeader
              label="Strategy"
              field="strategy_score"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('strategy_score')}
              className="hidden @5xl:table-cell"
            />
            <th className="hidden px-4 py-3 text-left text-xs font-medium text-theme-text-secondary @5xl:table-cell">
              AI Edge
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
              Result
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary">
              Actual
            </th>
            <SortableHeader
              label="Margin"
              field="margin"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('margin')}
              className="hidden @3xl:table-cell"
            />
            <SortableHeader
              label="Mode"
              field="analysis_mode"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('analysis_mode')}
              className="hidden @5xl:table-cell"
            />
            <SortableHeader
              label="Created"
              field="created_at"
              sortBy={sortBy as SortBy}
              onSort={onSort}
              icon={getSortIcon('created_at')}
            />
          </tr>
        </thead>
        <tbody>
          {(picks || []).map((pick, index) => (
            <tr
              key={pick.id}
              onClick={() => onRowClick(pick.id)}
              className={`cursor-pointer transition-colors hover:bg-theme-bg ${
                index % 2 === 0 ? 'bg-theme-surface' : 'bg-theme-bg/50'
              }`}
            >
              <td className="px-4 py-3">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-theme-text">
                    {pick.playerName || pick.team}
                  </span>
                  {pick.wasTracked && (
                    <Star className="h-3 w-3 fill-theme-warning text-theme-warning" />
                  )}
                  {pick.wasBetOn && <DollarSign className="h-3 w-3 text-theme-success" />}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-theme-text">
                <div>
                  {formatStatLabel(pick.propType)}
                  {pick.overUnder && (
                    <span className="ml-1 text-theme-text-tertiary">({pick.overUnder})</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-theme-text">{pick.line || '-'}</td>
              <td className="hidden px-4 py-3 text-sm text-theme-text @3xl:table-cell">
                {pick.predictedValue || '-'}
              </td>
              <td className="hidden px-4 py-3 text-sm text-theme-text @3xl:table-cell">
                {pick.edgePct ? `${pick.edgePct}%` : '-'}
              </td>
              <td className="hidden px-4 py-3 text-sm text-theme-text @5xl:table-cell">
                {pick.strategyScore || '-'}
              </td>
              <td className="hidden px-4 py-3 @5xl:table-cell">
                {pick.aiEdge && <AIEdgeBadge edge={pick.aiEdge} />}
              </td>
              <td className="px-4 py-3">
                <ResultBadge outcomeResolved={pick.outcomeResolved ?? false} hit={pick.hit} />
              </td>
              <td className="px-4 py-3 text-sm text-theme-text">{pick.actualValue || '-'}</td>
              <td className="hidden px-4 py-3 text-sm text-theme-text @3xl:table-cell">
                {pick.margin ? (
                  <span
                    className={
                      parseFloat(pick.margin) > 0 ? 'text-theme-success' : 'text-theme-danger'
                    }
                  >
                    {pick.margin}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td className="hidden px-4 py-3 @5xl:table-cell">
                {pick.analysisMode && <ModeBadge mode={pick.analysisMode} />}
              </td>
              <td className="px-4 py-3 text-sm text-theme-text-secondary">
                {format(new Date(pick.createdAt), 'MMM d, h:mma')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  field: SortBy | ParlaySort;
  sortBy: SortBy | ParlaySort;
  onSort: (field: SortBy | ParlaySort) => void;
  icon: React.ReactNode;
  className?: string;
}

function SortableHeader({
  label,
  field,
  sortBy,
  onSort,
  icon,
  className = '',
}: SortableHeaderProps) {
  const isActive = sortBy === field;

  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center space-x-1 px-4 py-3 text-left text-xs font-medium text-theme-text-secondary hover:text-theme-text"
      >
        <span className={isActive ? 'font-bold' : ''}>{label}</span>
        {icon}
      </button>
    </th>
  );
}

interface ResultBadgeProps {
  outcomeResolved: boolean;
  hit: boolean | null;
}

function ResultBadge({ outcomeResolved, hit }: ResultBadgeProps) {
  if (!outcomeResolved) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-warning/10 px-2 py-1 text-xs font-medium text-theme-warning">
        PENDING
      </span>
    );
  }

  if (hit === true) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-success/10 px-2 py-1 text-xs font-medium text-theme-success">
        HIT
      </span>
    );
  }

  if (hit === false) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-danger/10 px-2 py-1 text-xs font-medium text-theme-danger">
        MISS
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-theme-text-tertiary/10 px-2 py-1 text-xs font-medium text-theme-text-tertiary">
      PUSH
    </span>
  );
}

interface ParlayResultBadgeProps {
  outcomeResolved: boolean;
  parlayHit: boolean | null;
  hitCount: number;
  legCount: number;
  settledCount: number;
}

function ParlayResultBadge({
  outcomeResolved,
  parlayHit,
  hitCount,
  legCount: _legCount,
  settledCount,
}: ParlayResultBadgeProps) {
  if (!outcomeResolved) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-warning/10 px-2 py-1 text-xs font-medium text-theme-warning">
        PENDING
      </span>
    );
  }

  if (parlayHit === true) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-success/10 px-2 py-1 text-xs font-medium text-theme-success">
        HIT
      </span>
    );
  }

  if (parlayHit === false) {
    return (
      <span className="inline-flex items-center rounded-full bg-theme-danger/10 px-2 py-1 text-xs font-medium text-theme-danger">
        MISS
      </span>
    );
  }

  // Partial hit (some legs hit, some missed)
  if (settledCount > 0 && hitCount > 0 && hitCount < settledCount) {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-1 text-xs font-medium text-blue-400">
        PARTIAL ({hitCount}/{settledCount})
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-theme-text-tertiary/10 px-2 py-1 text-xs font-medium text-theme-text-tertiary">
      -
    </span>
  );
}

interface AIEdgeBadgeProps {
  edge: string;
}

function AIEdgeBadge({ edge }: AIEdgeBadgeProps) {
  const colors: Record<string, string> = {
    Strong: 'bg-theme-success/10 text-theme-success',
    Moderate: 'bg-theme-primary/10 text-theme-primary',
    Slight: 'bg-theme-text-tertiary/10 text-theme-text-tertiary',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
        colors[edge] || 'bg-theme-text-tertiary/10 text-theme-text-tertiary'
      }`}
    >
      {edge}
    </span>
  );
}

interface ModeBadgeProps {
  mode: string;
}

function ModeBadge({ mode }: ModeBadgeProps) {
  const colors: Record<string, string> = {
    premium: 'bg-theme-warning/10 text-theme-warning',
    detailed: 'bg-blue-500/10 text-blue-400',
    standard: 'bg-theme-text-tertiary/10 text-theme-text-tertiary',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
        colors[mode] || 'bg-theme-text-tertiary/10 text-theme-text-tertiary'
      }`}
    >
      {mode.toUpperCase()}
    </span>
  );
}
